import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { IN_APP_FOOTER_LINKS } from './publicFooterLinks';

// Modeled on kolega.dev/terms (KLG Tech Innovations Limited) and adapted for Kolega Studio.
// Paddle acts as Merchant of Record for payments — legal wording flagged for sign-off.
function PublicPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white flex flex-col">
      <header className="border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-base sm:text-lg font-bold flex items-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1.5 sm:mr-2">
              <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 8L21 12L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 20L14 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Kolega</span>
            <span className="font-light">Studio</span>
          </Link>
          <Link
            to="/"
            className="bg-brand-primary text-black text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-primaryDark transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="border-t border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
          {IN_APP_FOOTER_LINKS.map((link) => (
            <Link key={link.href} to={link.href} className="hover:text-neutral-900 dark:hover:text-white transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
        <div className="pb-6 text-center text-xs text-neutral-500 dark:text-neutral-500">
          &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default function TermsPage() {
  return (
    <PublicPageShell>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-neutral-700 dark:text-neutral-300">
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Terms of Service</h1>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-8">Last Updated: August 11, 2026</p>

        <div className="space-y-6 text-sm">
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">1. Introduction and Acceptance</h3>
            <p className="mb-3">
              Welcome to Kolega Studio, operated by KLG Tech Innovations Limited (&quot;Kolega,&quot; &quot;we,&quot; &quot;us&quot;).
              These Terms of Service (&quot;Terms&quot;) govern your use of our AI-assisted application building
              platform and services at studio.app.kolega.co.uk (the &quot;Service&quot;).
            </p>
            <p>
              By creating an account, accessing our platform, or using our Service, you agree to these Terms,
              our <Link to="/privacy" className="text-brand-primaryDark dark:text-brand-primary hover:underline">Privacy Policy</Link>,
              and all applicable laws. If you&apos;re using our Service for an organisation, you represent that you
              have authority to bind that organisation to these Terms.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">2. Service Description</h3>
            <p>
              Kolega Studio is an AI-assisted application building platform. The Service lets you create
              workspaces and projects in which AI agents help you design, build, and iterate on full-stack
              web applications. Our Service includes AI-powered code generation and editing, project file
              management, application preview and deployment/hosting, optional GitHub integration for
              exporting your code, and optional custom domain registration and management for your
              deployed applications.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">3. Eligibility</h3>
            <p className="mb-3">
              You must be at least 18 years old to use our Service. By registering, you confirm that:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>You meet the age requirement</li>
              <li>You haven&apos;t been previously banned from our Service</li>
              <li>Your use complies with all applicable laws</li>
              <li>You have authority to accept these Terms</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">4. Account Registration and Security</h3>
            <p className="mb-3">
              To access our Service, you must create an account with accurate, complete information.
              You&apos;re responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Maintaining current account information</li>
              <li>Protecting your login credentials</li>
              <li>All activities under your account</li>
              <li>Notifying us immediately of unauthorised access at contact@kolega.ai</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">5. Subscription Plans, Credits, and Pricing</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">5.1 Service Tiers</h4>
            <p className="mb-3">We offer the following subscription plans:</p>

            <div className="space-y-3">
              <div>
                <h5 className="font-medium text-neutral-900 dark:text-white">Creator ($20/month):</h5>
                <p>
                  Includes 2,000 monthly usage credits, AI-assisted application building, deployments and
                  hosting for your projects, and access to credit top-ups.
                </p>
              </div>

              <div>
                <h5 className="font-medium text-neutral-900 dark:text-white">Pro ($59/month — coming soon):</h5>
                <p>
                  A higher monthly credit allowance and additional capabilities for heavier usage. Not yet
                  available for signup; details will be published when the plan launches.
                </p>
              </div>

              <div>
                <h5 className="font-medium text-neutral-900 dark:text-white">Custom (Custom pricing):</h5>
                <p>
                  Custom credit allowances, usage arrangements, and support for teams and organisations with
                  specific requirements. Contact us to discuss a custom plan.
                </p>
              </div>
            </div>

            <p className="mt-3">
              New accounts receive a one-time grant of free signup credits (valid for 14 days) so you can
              try the Service before subscribing. Signup credits have no monetary value, are non-transferable,
              and expire automatically.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2 mt-4">5.2 Pricing and Currency</h4>
            <p className="mb-3">
              All fees are displayed in USD and are non-refundable except where required by law.
              Prices may be shown in other currencies based on your location at current exchange rates.
              Taxes may apply and will be calculated at checkout.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">5.3 Payment Terms</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>Monthly subscriptions auto-renew unless cancelled before the renewal date</li>
              <li>
                Payment processing is handled by Paddle, who acts as the Merchant of Record (MoR) for every
                transaction. Paddle (not Kolega) is the seller of record, appears on your billing statement, and is
                responsible for payment processing, invoicing, and global sales tax, VAT, and GST. See our{' '}
                <Link to="/refund-policy" className="text-brand-primaryDark dark:text-brand-primary hover:underline">Refund Policy</Link>{' '}
                for cancellation and refund details.
              </li>
              <li>You authorise Paddle to charge your payment method for all applicable fees</li>
              <li>Unpaid accounts may be suspended or terminated</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2 mt-4">5.4 Usage Credits</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>Subscriptions provide platform access, features, and a monthly allowance of usage credits</li>
              <li>
                Credits are consumed when you use AI features (such as building, editing, and iterating on your
                applications). Credit consumption is based on the underlying AI usage of each request and may
                include a margin over provider costs
              </li>
              <li>Included monthly credits reset each billing cycle and unused included credits do not roll over</li>
              <li>Additional credit top-ups may be purchased at any time; purchased top-up credits are consumed after included credits</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2 mt-4">5.5 Credit Top-Ups, Domains, and Overages</h4>
            <p>
              If you exhaust your monthly credit allowance, you may purchase credit top-ups to continue using AI
              features, or wait until your allowance resets at the next billing cycle. Custom domain registrations
              purchased through the Service are separate one-time purchases, subject to the applicable registry&apos;s
              terms, and are non-refundable once registration has started. Custom-plan customers may contact us for
              bespoke usage arrangements.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">6. Acceptable Use Policy</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">6.1 Prohibited Activities</h4>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Infringe intellectual property rights</li>
              <li>Violate any laws or regulations</li>
              <li>Harass, threaten, or harm others</li>
              <li>Distribute malware or harmful code</li>
              <li>Attempt unauthorised access to systems</li>
              <li>Scrape or extract data from our Service</li>
              <li>Interfere with Service operations</li>
              <li>Use the Service to develop competing products</li>
              <li>Submit sensitive regulated data (HIPAA, PCI-DSS, etc.)</li>
              <li>Build or deploy applications that are unlawful, deceptive, or harmful to others</li>
              <li>Abuse AI features to generate content or code for purposes prohibited by these Terms</li>
              <li>Use deployed applications or custom domains for spam, phishing, or malware distribution</li>
              <li>Impersonate others or misrepresent affiliations</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2 mt-4">6.2 Content Standards</h4>
            <p>
              All prompts, code, and content you submit to the Service, and all applications you build or deploy
              with it, must comply with applicable laws and must not contain material that is offensive,
              discriminatory, or harmful.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2 mt-4">6.3 Enforcement</h4>
            <p>
              Violations may result in warnings, suspension, or permanent termination at our discretion.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">7. Intellectual Property Rights</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">7.1 Our Property</h4>
            <p className="mb-3">
              The Service, including its design, features, and technology, remains our exclusive property.
              No rights are granted except those explicitly stated in these Terms.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">7.2 Your Content</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>You retain ownership of prompts, code, and content you provide to the Service (&quot;Input&quot;)</li>
              <li>You own the applications and code Kolega Studio generates for you (&quot;Output&quot;)</li>
              <li>You grant us a limited licence to process and host your Input and Output solely to provide the Service</li>
              <li>Similar Outputs may be generated for other users with similar requests</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2 mt-4">7.3 No Training Use</h4>
            <p>We do not use your Input or Output to train AI models.</p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">8. Beta Features</h3>
            <p>
              We may offer trial or beta features that are provided &quot;AS IS&quot; without warranties.
              These may be modified or discontinued at any time. Your use of beta features
              constitutes acceptance of these conditions.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">9. Third-Party Services</h3>
            <p>
              Our Service integrates with third-party platforms including AI providers (such as Anthropic and
              OpenAI) for code and content generation, GitHub for exporting your code to your own repositories,
              cloud infrastructure providers for hosting and deployments, and domain registrars for custom
              domain registration. Your use of these integrations is subject to their respective terms. We&apos;re
              not responsible for third-party services&apos; functionality or data practices.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">10. Privacy and Data Protection</h3>
            <p className="mb-3">
              Our Privacy Policy explains how we collect and use your information. Key points include:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We process data only to provide our Service</li>
              <li>Your project content is stored so your workspaces persist between sessions</li>
              <li>We implement industry-standard security measures</li>
              <li>Your data is not used for AI training</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">11. Service Modifications</h3>
            <p>
              We may modify, suspend, or discontinue Service features at any time. While we strive for
              reliability, we cannot guarantee uninterrupted access. You should maintain your own backups
              of important code and configurations, for example by exporting your projects to GitHub.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">12. Indemnification</h3>
            <p className="mb-3">
              You agree to defend and indemnify Kolega and its affiliates from any claims arising from:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your use of the Service</li>
              <li>Applications you build, deploy, or distribute using the Service</li>
              <li>Violations of these Terms</li>
              <li>Infringement of third-party rights</li>
              <li>Disputes with other parties</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">13. Disclaimers</h3>
            <p className="font-medium">
              THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL
              IMPLIED WARRANTIES INCLUDING MERCHANTABILITY AND FITNESS FOR PARTICULAR PURPOSE.
              AI-GENERATED APPLICATIONS AND CODE MAY CONTAIN ERRORS AND SHOULD BE REVIEWED AND TESTED
              BEFORE PRODUCTION USE. KOLEGA STUDIO DOES NOT GUARANTEE THAT GENERATED APPLICATIONS WILL
              BE FREE OF DEFECTS, SECURE, OR FIT FOR ANY PARTICULAR PURPOSE.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">14. Limitation of Liability</h3>
            <p className="mb-3 font-medium">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>We exclude liability for indirect, consequential, or punitive damages</li>
              <li>Our total liability is limited to fees paid in the 12 months before the claim or &euro;100, whichever is greater</li>
              <li>These limitations are fundamental to our Service offering</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">15. Termination</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">15.1 Termination Rights</h4>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>You may stop using our Service at any time</li>
              <li>We may terminate accounts for violations or at our discretion</li>
              <li>Termination doesn&apos;t relieve payment obligations</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">15.2 Effect of Termination</h4>
            <p className="mb-3">Upon termination:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your access ends immediately</li>
              <li>Outstanding fees remain due</li>
              <li>You may lose access to your workspaces, projects, deployments, and remaining credits</li>
              <li>Certain Terms survive (intellectual property, indemnification, limitations)</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">16. Dispute Resolution</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">16.1 Governing Law</h4>
            <p className="mb-3">
              These Terms are governed by UK law. The courts of the United Kingdom have exclusive jurisdiction.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">16.2 Dispute Process</h4>
            <p>
              We encourage resolving disputes through direct communication. Contact us at contact@kolega.ai
              to address concerns before pursuing formal proceedings.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">17. General Provisions</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">17.1 Complete Agreement</h4>
            <p className="mb-3">These Terms and referenced policies constitute our entire agreement.</p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">17.2 Updates</h4>
            <p className="mb-3">
              We may update these Terms with 30 days&apos; notice for material changes. Continued use constitutes acceptance.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">17.3 Assignment</h4>
            <p className="mb-3">
              You may not transfer these Terms. We may assign our rights and obligations.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">17.4 Electronic Communications</h4>
            <p>
              You consent to receive electronic communications which satisfy legal writing requirements.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">18. Contact Information</h3>
            <div className="bg-neutral-100 dark:bg-neutral-900 p-4 rounded-lg">
              <p className="font-medium text-neutral-900 dark:text-white">KLG Tech Innovations Limited</p>
              <p>Val Verclut, La Route des Cotils</p>
              <p>Grouville, JE3 9AP, Jersey</p>
              <p>Email: contact@kolega.ai</p>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">19. Additional Provisions</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">19.1 Export Compliance</h4>
            <p className="mb-3">
              The Service may be subject to export controls. You agree to comply with applicable trade regulations.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">19.2 Force Majeure</h4>
            <p className="mb-3">
              Neither party is liable for delays due to circumstances beyond reasonable control.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">19.3 Questions and Contact</h4>
            <p className="mb-3">
              All users may contact us with questions about these Terms or our Service using the contact information provided above.
            </p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">19.4 Support</h4>
            <p>We provide support at our discretion according to published policies.</p>
          </section>

          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
            &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
          </div>
        </div>
      </main>
    </PublicPageShell>
  );
}
